import React, {
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useNavigate } from 'react-router-dom'

import { HOME_PAGE } from 'constants/routes'

import { useAuth } from 'contexts/AuthProvider'

import { TimeoutModal } from 'components/TimeoutModal/TimeoutModal'

interface TimeoutContextType {
	getRemainingTime: () => number
	handleLogout: () => void
	isIdle: () => boolean
	lastActivity: number
	resetTimer: () => void
	setLastActivity: (time: number) => void
	setShowTimeout: (show: boolean) => void
	showTimeout: boolean
}

interface TimeoutProviderProps {
	children: ReactNode
	crossTab?: boolean
	timeoutDuration: number
	warningDuration: number
}

interface BroadcastMessage {
	timestamp: number
	type: 'SHOW_TIMEOUT' | 'HIDE_TIMEOUT' | 'ACTIVITY' | 'LOGOUT'
}

const TimeoutContext = createContext<TimeoutContextType | undefined>(undefined)

export const TimeoutProvider: React.FC<TimeoutProviderProps> = ({
	children,
	timeoutDuration,
	warningDuration,
	crossTab = true,
}) => {
	const [showTimeout, setShowTimeout] = useState(false)
	const [lastActivity, setLastActivity] = useState(Date.now())

	const { user, userSignOut } = useAuth()
	const navigate = useNavigate()

	const broadcastChannelRef = useRef<BroadcastChannel | null>(null)

	if (
		!broadcastChannelRef.current &&
		typeof window !== 'undefined' &&
		window.BroadcastChannel
	) {
		const channel = new BroadcastChannel('timeout-provider')
		broadcastChannelRef.current = channel

		const handleMessage = (event: MessageEvent<BroadcastMessage>) => {
			const { type, timestamp } = event.data

			switch (type) {
				case 'SHOW_TIMEOUT':
					setShowTimeout(true)
					break

				case 'HIDE_TIMEOUT':
					setShowTimeout(false)
					setLastActivity(timestamp)
					break

				case 'ACTIVITY':
					// Access current state through a callback
					setShowTimeout((current) => {
						if (!current) {
							setLastActivity(timestamp)
						}
						return current
					})
					break

				case 'LOGOUT':
					// Handle logout inline or call a stable function
					handleLogout()
					break
			}
		}

		channel.addEventListener('message', handleMessage)
	}

	useEffect(() => {
		return () => {
			if (broadcastChannelRef.current) {
				broadcastChannelRef.current.close()
			}
		}
	}, [])

	const broadcast = (message: BroadcastMessage) => {
		if (crossTab && broadcastChannelRef.current) {
			broadcastChannelRef.current.postMessage(message)
		}
	}

	const handleLogout = async () => {
		pause()
		setShowTimeout(false)
		resetTimer()

		broadcast({
			type: 'LOGOUT',
			timestamp: Date.now(),
		})

		userSignOut()
		navigate(HOME_PAGE)
	}

	const resetTimer = () => {
		const now = Date.now()
		setLastActivity(now)
		setShowTimeout(false)

		broadcast({
			type: 'HIDE_TIMEOUT',
			timestamp: now,
		})

		reset()
		activate()
	}

	const onIdle = () => {
		handleLogout()
	}

	const onPrompt = () => {
		setShowTimeout(true)

		broadcast({
			type: 'SHOW_TIMEOUT',
			timestamp: Date.now(),
		})
	}

	const onActive = () => {
		const now = Date.now()
		setLastActivity(now)
		setShowTimeout(false)

		broadcast({
			type: 'ACTIVITY',
			timestamp: now,
		})

		reset()
		activate()
	}

	const onAction = () => {
		const now = Date.now()
		setLastActivity(now)

		broadcast({
			type: 'ACTIVITY',
			timestamp: now,
		})
	}

	const { getRemainingTime, isIdle, reset, activate, pause, resume } =
		useIdleTimer({
			onIdle,
			onPrompt,
			onActive,
			onAction,
			timeout: timeoutDuration,
			promptTimeout: warningDuration,
			throttle: 500,
			crossTab,
			syncTimers: crossTab ? 200 : undefined,
			leaderElection: crossTab,
			events: [
				'mousedown',
				'mousemove',
				'keypress',
				'scroll',
				'touchstart',
				'click',
				'focus',
			],
		})

	useEffect(() => {
		if (user.session) {
			resume()
		} else {
			pause()
		}
	}, [user.session, resume, pause])

	useEffect(() => {
		return () => {
			if (broadcastChannelRef.current) {
				broadcastChannelRef.current.close()
			}
		}
	}, [])

	return (
		<TimeoutContext.Provider
			value={{
				showTimeout,
				resetTimer,
				handleLogout,
				lastActivity,
				setLastActivity,
				setShowTimeout,
				getRemainingTime,
				isIdle,
			}}
		>
			{children}
			{user.session && <TimeoutModal />}
		</TimeoutContext.Provider>
	)
}

export const useActivityTimeout = () => {
	const context = useContext(TimeoutContext)
	if (!context) {
		throw new Error(
			'useActivityTimeout must be used within a TimeoutProvider'
		)
	}
	return context
}
